import type { ReactNode } from 'react';
import type { ClinicalDataShareMessage } from '@features/components/Chat/components/ChatContent/Messages/types/chatMessages';
import { type MessageForPreview } from '../types';
import { ClinicalDataBadge } from './ClinicalDataBadge';
import { FileBadge } from './FileBadge';
import { MentionBadge } from './MentionBadge';
import { UnreadBadge } from './UnreadBadge';
import { HighlightedMessageText } from './HighlightedMessageText';

type PreviewMessageBodyProps = {
  latestMessagePreview?: ReactNode;
  message?: MessageForPreview | null;
  unread?: number;
  hasUnreadMentions: boolean;
  isMuted?: boolean;
  searchQuery?: string;
};

export const PreviewMessageBody = ({
  latestMessagePreview,
  message,
  unread,
  hasUnreadMentions,
  isMuted,
  searchQuery,
}: PreviewMessageBodyProps) => {
  const userName = message?.user?.name;
  const clinicalDataTitle = (message?.clinicalDataAttachment as ClinicalDataShareMessage | undefined)?.title;
  const firstAttachment = message?.attachments?.[0];
  const attachmentTitle = firstAttachment?.title ?? firstAttachment?.fallback ?? 'File';

  return (
    <span className="flex flex-col min-w-0 overflow-hidden">
      {userName && <span className="truncate text-base-10 text-s-m">{userName}:</span>}
      <span className="flex items-center gap-1 min-w-0 overflow-hidden">
        {clinicalDataTitle && <ClinicalDataBadge title={clinicalDataTitle} />}
        {!clinicalDataTitle && firstAttachment && <FileBadge title={attachmentTitle} />}
        <span className="truncate min-w-0 flex-1 text-base-20">
          <HighlightedMessageText text={message?.text} highlight={searchQuery} fallback={latestMessagePreview} />
        </span>
        {hasUnreadMentions && <MentionBadge />}
        {!!unread && <UnreadBadge count={unread} isMuted={isMuted} />}
      </span>
    </span>
  );
};
